// Snow inside a squishy globe. The water is a small Stable Fluids grid (Stam
// 1999; "Real-Time Fluid Dynamics for Games", GDC 2003): semi-Lagrangian
// advection, a Jacobi pressure projection, and vorticity confinement (Fedkiw,
// Stam and Jensen 2001) to keep swirls alive. Flakes ride the flow with drag,
// fall slowly, flutter in a divergence-free field, and settle on the floor.
// It is a visual model at game scale, not a measured fluid.
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));

export class SnowGlobe{
  constructor({center,radius,floor,count=320,grid=10,random=Math.random,iterations=14}={}){
    this.center=[...center];this.radius=radius;this.floor=floor??(()=>center[1]-radius);this.random=random;
    this.n=grid;this.h=2*radius/grid;this.min=center.map(v=>v-radius);this.iterations=iterations;
    const cells=grid**3;
    for(const key of ['u','v','w','u0','v0','w0','p','div','curl'])this[key]=new Float32Array(cells);
    this.cx=new Float32Array(cells);this.cy=new Float32Array(cells);this.cz=new Float32Array(cells);
    this.solid=new Uint8Array(cells);
    for(let k=0;k<grid;k++)for(let j=0;j<grid;j++)for(let i=0;i<grid;i++){
      const x=this.min[0]+(i+.5)*this.h,y=this.min[1]+(j+.5)*this.h,z=this.min[2]+(k+.5)*this.h;
      const inside=Math.hypot(x-center[0],y-center[1],z-center[2])<radius-this.h*.35&&y>this.floor(x,z)-this.h*.25;
      this.solid[this.index(i,j,k)]=inside?0:1;
    }
    this.count=count;this.flakeRadius=radius*.042;
    for(const key of ['px','py','pz','vx','vy','vz','size','spin','spinRate'])this[key]=new Float32Array(count);
    this.axis=new Float32Array(count*3);this.settled=new Uint8Array(count);
    this.time=0;this.activity=0;this.maxSpeed=0;this.airborne=0;
    for(let f=0;f<count;f++){
      const a=random()*Math.PI*2,b=Math.acos(2*random()-1);
      this.axis.set([Math.sin(b)*Math.cos(a),Math.cos(b),Math.sin(b)*Math.sin(a)],f*3);
      this.size[f]=.7+.6*random();this.spinRate[f]=(random()-.5)*7;this.spin[f]=random()*6.28;
    }
    this.settleAll();
  }
  index(i,j,k){return i+this.n*(j+this.n*k);}
  // A point on the floor inside the globe, for resting snow.
  floorPoint(f){
    const c=this.center,r=this.radius;
    for(let tries=0;tries<12;tries++){
      const a=this.random()*Math.PI*2,d=Math.sqrt(this.random())*r*.92;
      const x=c[0]+Math.cos(a)*d,z=c[2]+Math.sin(a)*d,y=this.floor(x,z)+this.flakeRadius*.6;
      if(Math.hypot(x-c[0],y-c[1],z-c[2])<r-this.flakeRadius*2){this.px[f]=x;this.py[f]=y;this.pz[f]=z;return;}
    }
    this.px[f]=c[0];this.pz[f]=c[2];this.py[f]=this.floor(c[0],c[2])+this.flakeRadius*.6;
  }
  settleAll(){
    for(let f=0;f<this.count;f++){this.floorPoint(f);this.vx[f]=this.vy[f]=this.vz[f]=0;this.settled[f]=1;}
    for(const key of ['u','v','w','u0','v0','w0','p'])this[key].fill(0);
    this.activity=0;this.maxSpeed=0;this.airborne=0;
  }
  reset(){this.settleAll();}
  // Trilinear sample of one velocity field at a world (toy-local) point.
  sample(field,x,y,z){
    const n=this.n,fx=clamp((x-this.min[0])/this.h-.5,0,n-1.001),fy=clamp((y-this.min[1])/this.h-.5,0,n-1.001),fz=clamp((z-this.min[2])/this.h-.5,0,n-1.001);
    const i=Math.floor(fx),j=Math.floor(fy),k=Math.floor(fz),a=fx-i,b=fy-j,c=fz-k;
    const at=(di,dj,dk)=>field[this.index(Math.min(n-1,i+di),Math.min(n-1,j+dj),Math.min(n-1,k+dk))];
    return (at(0,0,0)*(1-a)+at(1,0,0)*a)*(1-b)*(1-c)+(at(0,1,0)*(1-a)+at(1,1,0)*a)*b*(1-c)+(at(0,0,1)*(1-a)+at(1,0,1)*a)*(1-b)*c+(at(0,1,1)*(1-a)+at(1,1,1)*a)*b*c;
  }
  // Add velocity in a soft ball around a point (a finger stirring the water).
  inject(point,velocity,radius=this.radius*.45){
    if(!point?.every(Number.isFinite)||!velocity?.every(Number.isFinite))return;
    const n=this.n,limit=this.radius*6;
    const vel=velocity.map(v=>clamp(v,-limit,limit));
    for(let k=0;k<n;k++)for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const c=this.index(i,j,k);if(this.solid[c])continue;
      const x=this.min[0]+(i+.5)*this.h,y=this.min[1]+(j+.5)*this.h,z=this.min[2]+(k+.5)*this.h;
      const d=Math.hypot(x-point[0],y-point[1],z-point[2])/radius;if(d>=1)continue;
      const w=(1-d*d)**2;this.u[c]+=vel[0]*w;this.v[c]+=vel[1]*w;this.w[c]+=vel[2]*w;
    }
    this.wake();
  }
  // Shaking the globe: a swirl around the vertical axis with an updraft in the middle.
  shake(strength=1){
    const n=this.n,c0=this.center,r=this.radius;
    for(let k=0;k<n;k++)for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const c=this.index(i,j,k);if(this.solid[c])continue;
      const x=this.min[0]+(i+.5)*this.h-c0[0],y=this.min[1]+(j+.5)*this.h-c0[1],z=this.min[2]+(k+.5)*this.h-c0[2];
      const d=Math.hypot(x,z)/r;
      this.u[c]+=strength*r*4.4*(-z/r);this.w[c]+=strength*r*4.4*(x/r);this.v[c]+=strength*r*3.4*(1-d*1.6)*(1-y/r*.3);
    }
    // A gentle slosh lifts only some of the resting snow; a real shake lifts it all.
    const lift=Math.min(1,strength*2.5);
    for(let f=0;f<this.count;f++)if(!this.settled[f]||this.random()<lift){this.settled[f]=0;this.vy[f]+=strength*r*(2.6+this.random()*2.4);this.vx[f]+=strength*r*(this.random()-.5)*2;this.vz[f]+=strength*r*(this.random()-.5)*2;}
    this.wake();
  }
  wake(){this.activity=Math.max(this.activity,1);this.maxSpeed=Math.max(this.maxSpeed,.6);}
  step(dt){
    if(!Number.isFinite(dt)||dt<=0||this.activity===0)return;
    dt=Math.min(dt,1/30);this.time+=dt;
    this.vorticity(dt);this.advect(dt);this.project();
    const decay=Math.exp(-dt*.7),n3=this.n**3;let max=0;
    for(let c=0;c<n3;c++){this.u[c]*=decay;this.v[c]*=decay;this.w[c]*=decay;max=Math.max(max,Math.abs(this.u[c])+Math.abs(this.v[c])+Math.abs(this.w[c]));}
    if(max<.004){this.u.fill(0);this.v.fill(0);this.w.fill(0);max=0;}
    this.maxSpeed=max;this.moveFlakes(dt);
    this.activity=max>0||this.airborne>0?Math.max(max,.001):0;
  }
  vorticity(dt){
    // Confinement keeps swirls alive while stirred, then fades so the water can come to rest.
    const n=this.n,h=this.h,eps=1.6*Math.min(1,this.maxSpeed/.6),u=this.u,v=this.v,w=this.w;
    if(eps<=0)return;
    const at=(f,i,j,k)=>f[this.index(clamp(i,0,n-1),clamp(j,0,n-1),clamp(k,0,n-1))];
    for(let k=0;k<n;k++)for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const c=this.index(i,j,k);
      this.cx[c]=((at(w,i,j+1,k)-at(w,i,j-1,k))-(at(v,i,j,k+1)-at(v,i,j,k-1)))/(2*h);
      this.cy[c]=((at(u,i,j,k+1)-at(u,i,j,k-1))-(at(w,i+1,j,k)-at(w,i-1,j,k)))/(2*h);
      this.cz[c]=((at(v,i+1,j,k)-at(v,i-1,j,k))-(at(u,i,j+1,k)-at(u,i,j-1,k)))/(2*h);
      this.curl[c]=Math.hypot(this.cx[c],this.cy[c],this.cz[c]);
    }
    for(let k=1;k<n-1;k++)for(let j=1;j<n-1;j++)for(let i=1;i<n-1;i++){
      const c=this.index(i,j,k);if(this.solid[c])continue;
      let gx=(this.curl[c+1]-this.curl[c-1])/(2*h),gy=(this.curl[c+n]-this.curl[c-n])/(2*h),gz=(this.curl[c+n*n]-this.curl[c-n*n])/(2*h);
      const len=Math.hypot(gx,gy,gz);if(len<1e-6)continue;gx/=len;gy/=len;gz/=len;
      // f = eps h (N x omega)
      u[c]+=eps*h*(gy*this.cz[c]-gz*this.cy[c])*dt;v[c]+=eps*h*(gz*this.cx[c]-gx*this.cz[c])*dt;w[c]+=eps*h*(gx*this.cy[c]-gy*this.cx[c])*dt;
    }
  }
  advect(dt){
    const n=this.n;this.u0.set(this.u);this.v0.set(this.v);this.w0.set(this.w);
    for(let k=0;k<n;k++)for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const c=this.index(i,j,k);if(this.solid[c]){this.u[c]=this.v[c]=this.w[c]=0;continue;}
      const x=this.min[0]+(i+.5)*this.h-dt*this.u0[c],y=this.min[1]+(j+.5)*this.h-dt*this.v0[c],z=this.min[2]+(k+.5)*this.h-dt*this.w0[c];
      this.u[c]=this.sample(this.u0,x,y,z);this.v[c]=this.sample(this.v0,x,y,z);this.w[c]=this.sample(this.w0,x,y,z);
    }
  }
  project(){
    const n=this.n,h=this.h,s=this.solid,u=this.u,v=this.v,w=this.w,p=this.p,div=this.div;
    const val=(f,c,d,fallback)=>s[c+d]?fallback:f[c+d];
    for(let k=1;k<n-1;k++)for(let j=1;j<n-1;j++)for(let i=1;i<n-1;i++){
      const c=this.index(i,j,k);if(s[c]){div[c]=0;continue;}
      div[c]=(val(u,c,1,0)-val(u,c,-1,0)+val(v,c,n,0)-val(v,c,-n,0)+val(w,c,n*n,0)-val(w,c,-n*n,0))/(2*h);
    }
    p.fill(0);
    for(let iteration=0;iteration<this.iterations;iteration++)for(let k=1;k<n-1;k++)for(let j=1;j<n-1;j++)for(let i=1;i<n-1;i++){
      const c=this.index(i,j,k);if(s[c])continue;
      const q=p[c];p[c]=(val(p,c,1,q)+val(p,c,-1,q)+val(p,c,n,q)+val(p,c,-n,q)+val(p,c,n*n,q)+val(p,c,-n*n,q)-div[c]*h*h)/6;
    }
    for(let k=1;k<n-1;k++)for(let j=1;j<n-1;j++)for(let i=1;i<n-1;i++){
      const c=this.index(i,j,k);if(s[c])continue;const q=p[c];
      u[c]-=(val(p,c,1,q)-val(p,c,-1,q))/(2*h);v[c]-=(val(p,c,n,q)-val(p,c,-n,q))/(2*h);w[c]-=(val(p,c,n*n,q)-val(p,c,-n*n,q))/(2*h);
    }
  }
  moveFlakes(dt){
    const c0=this.center,limit=this.radius-this.flakeRadius*2,t=this.time,tau=.3,fall=.85;let airborne=0;
    for(let f=0;f<this.count;f++){
      let x=this.px[f],y=this.py[f],z=this.pz[f];
      const fu=this.sample(this.u,x,y,z),fv=this.sample(this.v,x,y,z),fw=this.sample(this.w,x,y,z),speed=Math.abs(fu)+Math.abs(fv)+Math.abs(fw);
      if(this.settled[f]){if(speed<.35){continue;}this.settled[f]=0;}
      // Divergence-free flutter (an ABC-style flow) so falling flakes drift and twirl.
      const s=this.radius*.18,nx=Math.sin(5.1*y+1.3*t)*Math.cos(4.3*z),ny=Math.sin(4.7*z+.9*t)*Math.cos(5.3*x)*.35,nz=Math.sin(5.9*x+1.1*t)*Math.cos(4.1*y);
      this.vx[f]+=((fu+nx*s-this.vx[f])/tau)*dt;this.vy[f]+=((fv+ny*s-this.vy[f])/tau-fall*this.radius)*dt;this.vz[f]+=((fw+nz*s-this.vz[f])/tau)*dt;
      x+=this.vx[f]*dt;y+=this.vy[f]*dt;z+=this.vz[f]*dt;
      const dx=x-c0[0],dy=y-c0[1],dz=z-c0[2],d=Math.hypot(dx,dy,dz);
      if(d>limit){const k=limit/d;x=c0[0]+dx*k;y=c0[1]+dy*k;z=c0[2]+dz*k;const vn=(this.vx[f]*dx+this.vy[f]*dy+this.vz[f]*dz)/d;if(vn>0){this.vx[f]-=vn*dx/d;this.vy[f]-=vn*dy/d;this.vz[f]-=vn*dz/d;}}
      const ground=this.floor(x,z)+this.flakeRadius*.6;
      if(y<=ground){y=ground;if(this.vy[f]<0)this.vy[f]=0;if(speed<.3){this.settled[f]=1;this.vx[f]=this.vy[f]=this.vz[f]=0;}}
      this.px[f]=x;this.py[f]=y;this.pz[f]=z;this.spin[f]+=this.spinRate[f]*dt;
      if(!this.settled[f])airborne++;
    }
    this.airborne=airborne;
  }
}
